/* ============================================================
   androidAjar.js — "AJAR ROBOT": pelanggan tunjuk butang, robot ingat
   ============================================================
   Keputusan user 4 Nov 2026 (PLAN_PHONE_TIKTOK_FB.md §STRATEGI):
   - Default admin dahulu. Phone pelanggan berbeza rupa → robot tersekat →
     BERHENTI, tunjuk skrin, minta pelanggan tunjuk butang → robot tekan →
     SAHKAN skrin seterusnya → baru SIMPAN dalam akaun pelanggan.
   - Disimpan DALAM AKAUN (pilihan user): users/{uid}/phoneAjar/{app__kunci}
     (subkoleksi — bukan medan dalam doc users: had 1 MB). Rules: pemilik
     baca/tulis, admin baca (Robot health nanti).
   - Ajaran HANYA untuk langkah yang diajar; langkah lain ikut default admin.
   Tangga cari setiap butang: ajaran pelanggan → default robot → minta manusia.
   ⛔ Robot tidak meneka berulang kali: manusia diminta selepas default gagal.
   ============================================================ */

import { collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { delay, tunggu, bacaSkrinSelamat, ketuk } from './androidDumpKit';
import { elementAt, klikPembungkus, cadangSelector } from './androidInspect';

const KOL = 'phoneAjar';
export const idAjar = (appId, kunci) => `${appId}__${kunci}`.replace(/[^A-Za-z0-9_.-]/g, '_').slice(0, 120);

// Semua ajaran pelanggan untuk satu app → { [kunci]: { sasaran, label, langkah, ... } }
export async function muatAjaran(appId, uid = auth.currentUser?.uid) {
  if (!uid || !appId) return {};
  try {
    const snap = await getDocs(collection(db, 'users', uid, KOL));
    const out = {};
    snap.forEach((d) => { const v = d.data() || {}; if (v.appId === appId && v.kunci) out[v.kunci] = v; });
    return out;
  } catch { return {}; } // tiada akses / luar talian → robot guna default sahaja
}

export async function simpanAjaran(appId, kunci, { label = '', langkah = '', sasaran, versiApp = '', model = '' }, uid = auth.currentUser?.uid) {
  if (!uid) throw new Error('Not signed in');
  if (!sasaran || typeof sasaran !== 'object') throw new Error('No target to save');
  const data = {
    appId: String(appId).slice(0, 20), kunci: String(kunci).slice(0, 80),
    label: String(label).slice(0, 80), langkah: String(langkah).slice(0, 80),
    sasaran, versiApp: String(versiApp).slice(0, 40), model: String(model).slice(0, 60),
    dikemas: serverTimestamp(),
  };
  await setDoc(doc(db, 'users', uid, KOL, idAjar(appId, kunci)), data);
  return data;
}

export async function buangAjaran(appId, kunci, uid = auth.currentUser?.uid) {
  if (!uid) return;
  await deleteDoc(doc(db, 'users', uid, KOL, idAjar(appId, kunci)));
}

/* Sasaran yang disimpan dari titik yang pelanggan tunjuk. Nama (desc/text/id) dahulu
   kalau UNIK pada skrin itu; SENTIASA bawa nisbah sebagai sandaran (skrin tak boleh dibaca). */
export function sasaranDariTitik(els, titik, skrin) {
  const w = skrin?.w || 720; const h = skrin?.h || 1640;
  const asas = { rx: +(titik.x / w).toFixed(4), ry: +(titik.y / h).toFixed(4) };
  const el0 = elementAt(els || [], titik.x, titik.y);
  const el = el0 && (el0.clickable ? el0 : (klikPembungkus(els, el0) || el0));
  if (!el) return asas;
  const c = cadangSelector(el, els, { w, h });
  const nama = {};
  if (c && c.unik === 1) {
    if (c.jenis === 'desc') nama.desc = c.nilai;
    else if (c.jenis === 'text') nama.text = c.nilai;
    else if (c.jenis === 'id') nama.id = c.nilai;
    else if (c.jenis === 'id+text') { nama.id = c.nilai.id; nama.text = c.nilai.text; }
  }
  return { ...nama, ...asas };
}

// Cari elemen padan sasaran pada skrin. Pulang { el?, x, y } atau null.
export function padanSasaran(els, s, skrin) {
  if (!s) return null;
  const w = skrin?.w || 720; const h = skrin?.h || 1640;
  const adaNama = s.desc || s.text || s.id;
  if (adaNama && els) {
    const el = els.find((e) => (!s.desc || e.desc === s.desc) && (!s.text || e.text === s.text) && (!s.id || e.id === s.id));
    if (el) return { el, x: Math.round((el.b.x1 + el.b.x2) / 2), y: Math.round((el.b.y1 + el.b.y2) / 2) };
    return null; // ada nama tapi tiada pada skrin → bukan skrin ini lagi
  }
  if (Number.isFinite(s.rx) && Number.isFinite(s.ry)) return { x: Math.round(s.rx * w), y: Math.round(s.ry * h) };
  return null;
}

/* Penyasar untuk robot. ctx = {
     ctl, appId, ajaran (peta dari muatAjaran), mintaAjar (async ({kunci,label,langkah,dataUrl,skrin}) → {x,y}|null),
     log, paksaGagal (Set kunci — UJIAN: anggap default tak jumpa) }
   ketukSasar(kunci, { label, langkah, pred, pos, ms, sahkan })
     pred  = cari elemen default (skrin boleh dibaca) · pos = [rx, ry] default kedudukan
     sahkan = async () => true bila skrin seterusnya betul
   Pulang { ok, sumber: 'ajaran'|'default'|'manusia', el? } */
export function buatPenyasar(ctx = {}) {
  const { ctl, appId } = ctx;
  const skrin = () => ({ w: ctl?.info?.screenWidth || 720, h: ctl?.info?.screenHeight || 1640 });
  const log = typeof ctx.log === 'function' ? ctx.log : () => {};
  const ajaran = ctx.ajaran || {};

  async function tekan(pt) {
    if (pt.el) await ketuk(ctl, pt.el, 1200);
    else { await ctl.tap(pt.x, pt.y); await delay(1200); }
  }

  async function ketukSasar(kunci, { label = kunci, langkah = '', pred = null, pos = null, ms = 8000, sahkan = null } = {}) {
    const lulus = async () => (sahkan ? !!(await sahkan()) : true);
    // 1. Ajaran pelanggan.
    const aj = ajaran[kunci]?.sasaran;
    if (aj) {
      let pt = null;
      for (const hingga = Date.now() + Math.min(ms, 6000); !pt && Date.now() < hingga;) {
        pt = padanSasaran(await bacaSkrinSelamat(ctl), aj, skrin());
        if (!pt) await delay(700);
      }
      if (pt) { await tekan(pt); if (await lulus()) return { ok: true, sumber: 'ajaran', el: pt.el }; log(`Ajaran "${label}" tidak menjadi — cuba default.`, 'warn'); }
    }
    // 2. Default robot.
    if (!ctx.paksaGagal?.has(kunci)) {
      let r = null;
      if (pred) {
        r = await tunggu(ctl, pred, ms);
        if (r) { await ketuk(ctl, r.el, 1200); if (await lulus()) return { ok: true, sumber: 'default', el: r.el }; }
      }
      if (!r && pos) { // kedudukan = sandaran (skrin tak boleh dibaca / nama tiada)
        const w = skrin(); await ctl.tap(Math.round(pos[0] * w.w), Math.round(pos[1] * w.h)); await delay(1500);
        if (await lulus()) return { ok: true, sumber: 'default' };
      }
    }
    // 3. Minta manusia (Ajar robot) — maksimum 2 kali, setiap kali disahkan.
    if (typeof ctx.mintaAjar !== 'function') return { ok: false };
    for (let cuba = 0; cuba < 2; cuba++) {
      let dataUrl = '';
      try { dataUrl = (await ctl.screenshot())?.dataUrl || ''; } catch { /* biar */ }
      const els = (await bacaSkrinSelamat(ctl)) || [];
      const titik = await ctx.mintaAjar({ kunci, label, langkah, dataUrl, skrin: skrin(), cubaan: cuba + 1 });
      if (!titik) return { ok: false, dibatal: true };
      const sasaran = sasaranDariTitik(els, titik, skrin());
      const el0 = elementAt(els, titik.x, titik.y);
      await tekan({ x: titik.x, y: titik.y });
      if (await lulus()) {
        try {
          await simpanAjaran(appId, kunci, { label, langkah, sasaran, model: ctl?.info?.model || '' });
          ajaran[kunci] = { sasaran, label, langkah };
          log(`Robot belajar "${label}" — disimpan dalam akaun anda.`, 'ok');
        } catch (e) {
          log(`Tak dapat simpan ajaran "${label}": ${e?.message || e}`, 'err');
        }
        return { ok: true, sumber: 'manusia', el: el0 };
      }
      log(`Selepas tekan "${label}", skrin seterusnya tak seperti dijangka — tunjuk sekali lagi.`, 'warn');
    }
    return { ok: false };
  }

  return { ketukSasar, ajaran };
}
